import { useCustomFetch } from '@/composables/useCustomFetch'

export interface Address {
  district: string
  province: string
  area: string
  country: string
  locality: string
  street: string
  house: string
  metro: string
}

export interface Geoobject {
  coordinates: number[]
  address: Address
}

type ReverseGeocodeResponse = { address: Address }
type DirectGeocodeResponse = { address: Address; coordinates: number[] }

/** Получение текущей позиции из браузера */
export const getMyPosition = (): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Геолокация не поддерживается вашим браузером'))
      return
    }

    navigator.geolocation.getCurrentPosition(resolve, reject)
  })

/** Прямое геокодирование: адрес -> координаты */
async function directGeocoding(
  city: string,
  query: string,
): Promise<Geoobject> {
  const customFetch = useCustomFetch(3)

  const data = await customFetch<DirectGeocodeResponse>(
    `${city}/geo/coordinates/`,
    {
      params: { address: query },
    },
  )

  return {
    address: data.address,
    coordinates: data.coordinates,
  }
}

/** Обратное геокодирование: координаты -> адрес */
async function reverseGeocoding(
  city: string,
  query: number[],
): Promise<Geoobject> {
  if (
    !Array.isArray(query) ||
    query.length < 2 ||
    query.some((n) => typeof n !== 'number' || Number.isNaN(n))
  ) {
    throw new TypeError('Ожидался массив координат вида [lon, lat]')
  }

  const customFetch = useCustomFetch(3)

  const data = await customFetch<ReverseGeocodeResponse>(
    `${city}/geo/address/`,
    {
      params: { ll: query.join(',') },
    },
  )

  return {
    address: data.address,
    coordinates: query,
  }
}

/** Фабрика сервиса геокодинга */
export function getGeocodeService(city: string) {
  async function geocode(query: string): Promise<Geoobject>
  async function geocode(query: number[]): Promise<Geoobject>
  async function geocode(query: string | number[]): Promise<Geoobject> {
    if (typeof query === 'string') return directGeocoding(city, query)
    if (Array.isArray(query)) return reverseGeocoding(city, query)
    throw new TypeError(`Неправильный формат запроса: ${String(query)}`)
  }

  return { geocode }
}
